/**
 * Block 2 (#1892) — the TARGET POPULATION: who the next pass would mail.
 *
 * This is a FORECAST and it is captioned as one. The send history next door is
 * a record of the past, and the single most damaging way to read this page is
 * to confuse the two: acting on "we already told these customers" when the
 * truth is "we are about to" (or the reverse) is a decision made against the
 * wrong tense. Hence the separate tab, the separate caption, and the banner
 * that restates the tense above the rows.
 *
 * Nothing here is stored. The route runs the send path's own criteria with the
 * mailer and the writer removed, so looking is free and looking again is the
 * normal way to check whether a mute landed.
 */
import type { AccountBalanceAlertPreviewResponse } from '@nadoutong/contracts';
import { formatLocalTimestamp, formatUsdCents } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { severityLabel, verdictLabel, verdictTone, wouldMail } from './balanceAlertStatus.ts';

interface Props {
  readonly preview: AccountBalanceAlertPreviewResponse;
  readonly onSuppress: (accountId: string, email: string) => void;
  readonly suppressingAccountId: string | null;
}

export function BalanceAlertPreviewTable({
  preview,
  onSuppress,
  suppressingAccountId,
}: Props): JSX.Element {
  const t = useT();
  const mailable = preview.entries.filter((entry) =>
    wouldMail({
      alertsEnabled: preview.alerts_enabled,
      suppressed: entry.suppressed,
      verdict: entry.verdict,
      activeAlertSeverity: entry.active_alert_severity,
    }),
  ).length;

  return (
    <div className="flex flex-col gap-3">
      {/* The tense, restated where the rows are. A list of twelve breaching
          accounts read as "twelve mails go out tonight" is the obvious mistake,
          and it is wrong twice over while the channel is closed. */}
      <p
        className={
          preview.alerts_enabled
            ? 'm-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper-mid'
            : 'm-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn'
        }
        data-testid="balance-alerts-preview-banner"
      >
        {preview.alerts_enabled
          ? t('balance_alerts.preview.banner_open', { count: String(mailable) })
          : t('balance_alerts.preview.banner_closed', { count: String(preview.entries.length) })}
      </p>

      <Table testId="balance-alerts-preview-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('balance_alerts.column.account')}</Th>
            <Th>{t('balance_alerts.column.verdict')}</Th>
            <Th num>{t('balance_alerts.column.spendable')}</Th>
            <Th num>{t('balance_alerts.column.days_left')}</Th>
            <Th num>{t('balance_alerts.column.burn')}</Th>
            <Th>{t('balance_alerts.column.next_pass')}</Th>
            <Th>{t('balance_alerts.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {preview.entries.length === 0 ? (
            <TableEmpty colSpan={7}>{t('balance_alerts.preview.empty')}</TableEmpty>
          ) : (
            preview.entries.map((entry) => (
              <Tr key={entry.account_id} interactive={false} data-testid={`balance-alerts-preview-row-${entry.account_id}`}>
                <Td emphasis>
                  <span className="flex flex-col">
                    <span>{entry.account_email}</span>
                    <span className="font-mono text-micro text-paper-low">{entry.account_id}</span>
                  </span>
                </Td>
                <Td>
                  <Chip
                    tone={verdictTone(entry.verdict)}
                    testId={`balance-alerts-preview-verdict-${entry.account_id}`}
                  >
                    {t(verdictLabel(entry.verdict))}
                  </Chip>
                </Td>
                <Td num mono>
                  {formatUsdCents(
                    entry.spendable_balance_usd_cents,
                    entry.spendable_balance_usd_cents < 0,
                  )}
                </Td>
                <Td num mono>
                  {entry.days_left === null ? '—' : entry.days_left}
                </Td>
                <Td num mono>
                  {t('balance_alerts.preview.burn_per_day', {
                    amount: formatUsdCents(entry.burn_per_day_usd_cents, false),
                  })}
                </Td>
                <Td>
                  <NextPassCell entry={entry} alertsEnabled={preview.alerts_enabled} />
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={entry.suppressed}
                    loading={suppressingAccountId === entry.account_id}
                    onClick={() => onSuppress(entry.account_id, entry.account_email)}
                    data-testid={`balance-alerts-suppress-${entry.account_id}`}
                  >
                    {t('balance_alerts.preview.suppress')}
                  </Button>
                </Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>

      <p className="m-0 text-dense text-paper-low" data-testid="balance-alerts-preview-window">
        {t('balance_alerts.preview.window', {
          days: String(preview.burn_basis_days),
          from: formatLocalTimestamp(preview.window_from),
        })}
      </p>
    </div>
  );
}

/**
 * What the next pass does with this row — the one column an operator actually
 * decides from.
 *
 * A breaching verdict does NOT imply a mail: the channel may be closed, the
 * account may be muted, or the dedup table may already hold a severity at or
 * above this verdict, in which case the pass stays silent because the customer
 * has already been told. Each of those reads as "nothing happens" from the
 * outside and they are four different situations, so the cell names which one.
 */
function NextPassCell({
  entry,
  alertsEnabled,
}: {
  readonly entry: AccountBalanceAlertPreviewResponse['entries'][number];
  readonly alertsEnabled: boolean;
}): JSX.Element {
  const t = useT();
  const testId = `balance-alerts-next-pass-${entry.account_id}`;

  if (entry.suppressed) {
    return (
      <Chip tone="neutral" testId={testId}>
        {t('balance_alerts.next_pass.suppressed')}
      </Chip>
    );
  }
  if (!alertsEnabled) {
    return (
      <Chip tone="warn" testId={testId}>
        {t('balance_alerts.next_pass.channel_closed')}
      </Chip>
    );
  }
  if (
    !wouldMail({
      alertsEnabled,
      suppressed: entry.suppressed,
      verdict: entry.verdict,
      activeAlertSeverity: entry.active_alert_severity,
    })
  ) {
    return (
      <Chip tone="neutral" testId={testId}>
        {t('balance_alerts.next_pass.already_alerted', {
          severity:
            entry.active_alert_severity === null
              ? ''
              : t(severityLabel(entry.active_alert_severity)),
        })}
      </Chip>
    );
  }
  return (
    <Chip tone="signal" testId={testId}>
      {entry.active_alert_severity === null
        ? t('balance_alerts.next_pass.mails')
        : t('balance_alerts.next_pass.escalates')}
    </Chip>
  );
}
